import { useState } from "react";
import { useSchemaForm } from "@/components/form";
import { notify } from "@/shared/notification";
import { useAddDocumentoElectronicoMutation, useCompletarDocumentoElectronicoMutation } from "@/features/facturacion-electronica/api/add-documento-electronico";
import { DocumentoElectronicoForm, documentoElectronicoFormSchema } from "@/features/facturacion-electronica/components/documento-electronico-form";
import { useEmisorConfig } from "@/features/facturacion-electronica/hooks/use-emisor-config";
import { buildFacturaElectronicaRequest } from "@/features/facturacion-electronica/utils/de-request-builder";
import { DEPageLayout } from "@/features/facturacion-electronica/components/de-page-layout";
import { DE_SUCCESS_MESSAGES } from "@/features/facturacion-electronica/utils/de-form-utils";
import { openPdfFromBase64 } from "@/features/facturacion-electronica/utils/pdf-utils";
import { Cliente } from "@/shared/types/cliente";
import { TipoOperacionEnum } from "@/shared/types/contribuyente";
import useMonetizacion from "@/features/monetizacion/hooks/use-monetizacion";
import { useNavigate, useSearchParams } from "react-router";

export default function NuevaFacturaElectronicaPage() {
    const [searchParams] = useSearchParams();
    const reservadoId = searchParams.get("reservadoId") ?? undefined;

    return (
        <DEPageLayout title={reservadoId ? "Completar Factura Electrónica Reservada" : "Nueva Factura Electrónica"}>
            <AddDocumentoElectronicoForm reservadoId={reservadoId} />
        </DEPageLayout>
    );
}

function AddDocumentoElectronicoForm({ reservadoId }: { reservadoId?: string }) {
    const navigate = useNavigate();
    const addMutation = useAddDocumentoElectronicoMutation();
    const completarMutation = useCompletarDocumentoElectronicoMutation(reservadoId ?? "");
    const mutation = reservadoId ? completarMutation : addMutation;
    const { config } = useEmisorConfig();
    const [cliente, setCliente] = useState<Cliente | null>(null);
    const { monedaBase, monedas, cotizacion } = useMonetizacion();

    const handleClienteChange = (nuevoCliente: Cliente | null) => {
        setCliente(nuevoCliente);
        const esB2G = nuevoCliente?.tipoOperacion === TipoOperacionEnum.B2G;
        form.setFieldValue("esB2G", esB2G);
        if (!esB2G) {
            form.setFieldValue("dModCont", undefined);
            form.setFieldValue("dEntCont", undefined);
            form.setFieldValue("dAnoCont", undefined);
            form.setFieldValue("dSecCont", undefined);
            form.setFieldValue("dFeCodCont", null);
        }
    };

    const form = useSchemaForm({
        schema: documentoElectronicoFormSchema,
        onSubmit: async ({ value, formApi }) => {
            const deRequest = buildFacturaElectronicaRequest(value, config!, monedaBase, monedas, cotizacion);
            const result = await mutation.mutateAsync(deRequest);
            notify(reservadoId ? "Factura electrónica completada correctamente" : DE_SUCCESS_MESSAGES.factura);

            if (result.kuDEBase64)
                openPdfFromBase64(result.kuDEBase64);

            if (reservadoId) {
                navigate("/documentos-electronicos");
                return;
            }

            // Reset form
            formApi.reset();
            setCliente(null);
            formApi.setFieldValue("clienteId", "");
            formApi.setFieldValue("items", []);
            formApi.setFieldValue("indicadorPresencia", "1");
            formApi.setFieldValue("condicionOperacion", "contado");
            formApi.setFieldValue("pagos", [{ tipoPago: "efectivo", monto: 0 }]);
            formApi.setFieldValue("esB2G", false);
        },
    });

    return (
        <DocumentoElectronicoForm
            form={form}
            onClienteChange={handleClienteChange}
            cliente={cliente}
            flex={1}
        />
    );
}
